import { Dispatch, FC, SetStateAction, SyntheticEvent, useEffect, useState } from 'react';

import { FormLabel } from '@mui/material';
import Autocomplete, {
    AutocompleteChangeDetails,
    AutocompleteChangeReason,
    autocompleteClasses,
} from '@mui/material/Autocomplete';
import Popper from '@mui/material/Popper';
import { styled } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { GridFilterItem } from '@mui/x-data-grid';
import { DEFAULT_LOCALE } from '@npi/buddy-utils';
import { useField } from 'formik';

import { AutoCompleteOptionType, ListBox } from '.';

const StyledPopper = styled(Popper)({
    [`& .${autocompleteClasses.listbox}`]: {
        boxSizing: 'border-box',
        '& ul': {
            padding: 0,
            margin: 0,
        },
    },
});

type NPIAutoCompleteProps = {
    name: string;
    options: AutoCompleteOptionType[];
    header?: string;
    placeholder?: string;
    disabled?: boolean;
    multiple?: boolean;
    useFormik?: boolean;
    required?: boolean;
    controlledValue?: AutoCompleteOptionType;
    onChange?: (value: AutoCompleteOptionType | AutoCompleteOptionType[]) => void;
    setSelectedValue?: Dispatch<SetStateAction<AutoCompleteOptionType | AutoCompleteOptionType[]>>;
    onInputChange?: (value: string) => void;
    // MUI DataGrid FilterItem usage for filter operator input
    item?: GridFilterItem;
    applyValue?: (value: GridFilterItem) => void;
    dataTestId?: string;
    onClear?: () => void;
    sortBy?: keyof AutoCompleteOptionType;
};

const DEFAULT_LOCALE_COMPARE_OPTIONS = { ignorePunctuation: true };

const NPIAutoComplete: FC<NPIAutoCompleteProps> = ({
    name,
    options,
    header,
    placeholder,
    disabled,
    multiple = false,
    useFormik = true,
    required = false,
    controlledValue,
    onClear,
    onChange = (_value: AutoCompleteOptionType | AutoCompleteOptionType[]) => {},
    setSelectedValue = (value: AutoCompleteOptionType[]) => {
        return value;
    },
    onInputChange = (_value: string) => {},
    item,
    applyValue,
    dataTestId = `${name}-autocomplete`,
    sortBy = 'label',
}) => {
    const [{ value: formikValue }, { error, touched }, { setValue }] = useFormik
        ? useField(name)
        : [{ value: null }, { error: null, touched: false }, { setValue: () => {} }];

    const [localValue, setLocalValue] = useState<AutoCompleteOptionType | AutoCompleteOptionType[]>(
        multiple ? [] : null
    );

    const value = useFormik ? formikValue : localValue;

    const setValueHandler = useFormik ? setValue : setLocalValue;

    const hasError = (error && touched) || (required && !value);

    const borderColor = hasError ? 'red' : '#F39E4E';

    const handleChange = (
        _event: SyntheticEvent<Element, Event>,
        value: any,
        _reason: AutocompleteChangeReason,
        _details?: AutocompleteChangeDetails<any>
    ) => {
        if (
            multiple &&
            value?.some((option: AutoCompleteOptionType) => {
                return option.label === 'ALL';
            })
        ) {
            const filteredOptions = options.filter((option: AutoCompleteOptionType) => {
                return option.label !== 'ALL';
            });
            setValueHandler(filteredOptions);
            if (onChange) {
                onChange(filteredOptions);
            }
            if (setSelectedValue) {
                setSelectedValue(filteredOptions);
            }
            return;
        }

        if (_reason === 'clear' && onClear) {
            onClear();
        }

        setValueHandler(value);

        if (onChange) {
            onChange(value);
        }

        if (setSelectedValue) {
            setSelectedValue(value);
        }

        // MUI DataGrid FilterItem
        if (applyValue) {
            applyValue({
                ...item,
                value,
            });
        }
    };

    const handleInputChange = (_event: SyntheticEvent<Element, Event>, value: string) => {
        if (onInputChange) {
            onInputChange(value);
        }
    };

    const ErrorMessage = ({ error }: { error: string }) => {
        return (
            <Typography color="error" variant="caption" style={{ marginTop: '8px' }}>
                {error}
            </Typography>
        );
    };

    useEffect(() => {
        setLocalValue(controlledValue);
    }, [controlledValue]);

    const sortOptions = (opts: AutoCompleteOptionType[]) => {
        if (sortBy === 'label') {
            return opts.sort((optionA, optionB) => {
                return optionA.label.localeCompare(
                    optionB.label,
                    DEFAULT_LOCALE,
                    DEFAULT_LOCALE_COMPARE_OPTIONS
                );
            });
        }

        return opts.sort((optionA, optionB) => {
            const valA = optionA[sortBy];
            const valB = optionB[sortBy];
            if (typeof valA === 'number' && typeof valB === 'number') {
                return valA - valB;
            }
            return String(valA).localeCompare(String(valB));
        });
    };

    return (
        <>
            {header && (
                <FormLabel
                    htmlFor={`${name}-autocomplete`}
                    sx={{ marginBottom: '8px', color: 'grey.900', fontWeight: 600 }}
                >
                    {header}
                </FormLabel>
            )}

            <Autocomplete
                id={`${name}-autocomplete`}
                data-testid={dataTestId}
                renderInput={(params) => {
                    return (
                        <TextField
                            {...params}
                            placeholder={disabled ? '' : placeholder}
                            sx={
                                disabled
                                    ? {}
                                    : {
                                          minWidth: '192px',
                                          color: 'grey.900',
                                          bgcolor: 'white',
                                          '& .MuiOutlinedInput-notchedOutline': {
                                              borderColor: `${borderColor} !important`,
                                          },
                                          '::placeholder': {
                                              color: 'grey.500',
                                          },
                                          borderRadius: '8px',
                                      }
                            }
                        />
                    );
                }}
                disableListWrap
                PopperComponent={StyledPopper}
                ListboxComponent={ListBox}
                disabled={disabled}
                multiple={multiple}
                value={value}
                onChange={handleChange}
                onInputChange={handleInputChange}
                options={
                    multiple
                        ? sortOptions(
                              options?.filter((option) => {
                                  return (
                                      Array.isArray(value) &&
                                      !value.some((selected) => {
                                          return selected.label === option.label;
                                      })
                                  );
                              }) || []
                          )
                        : sortOptions(options || [])
                }
                renderOption={(props, option) => {
                    return [props, option];
                }}
            />
            {hasError && <ErrorMessage error={error ?? 'Required'} />}
        </>
    );
};

export default NPIAutoComplete;
